import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../layout/Layout";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getAllImageUrls, getFirstImageUrl } from "../utils/imageUtils";
import { loadCSS } from "../utils/cssLoader";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Debug logging
  console.log('PropertyDetail component rendered with ID:', id);
  console.log('ID type:', typeof id);
  console.log('Current URL:', window.location.href);
  
  // Validate ID early
  const isValidId = id && id !== 'undefined' && id !== 'null' && !isNaN(parseInt(id));
  console.log('Is valid ID:', isValidId);

  // Load CSS files for PropertyDetail page
  useEffect(() => {
    loadCSS('PropertyDetail.css');
  }, []);

  // Immediate redirect for invalid IDs
  useEffect(() => {
    if (!isValidId) {
      console.error('❌ Invalid property ID detected, redirecting...');
      navigate('/properties', { replace: true });
    }
  }, [isValidId, navigate]);

  // Share functionality
  const shareProperty = async (platform) => {
    const url = window.location.href;
    const title = `${property.titulo} - Inmuebles Orsan`;
    const text = `¡Mira esta increíble propiedad! ${property.titulo}`;
    
    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: text,
          url: url
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      let shareUrl = '';
      switch (platform) {
        case 'whatsapp':
          shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
          break;
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
          break;
        case 'twitter':
          shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
          break;
        case 'copy':
          navigator.clipboard.writeText(url);
          alert('¡Enlace copiado al portapapeles!');
          setShowShareMenu(false);
          return;
        default:
          break;
      }
      if (shareUrl) {
        window.open(shareUrl, '_blank');
        setShowShareMenu(false);
      }
    }
  };

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        console.log('Fetching property with ID:', id);
        console.log('ID type:', typeof id);
        console.log('Current URL:', window.location.href);
        
        if (!id || id === 'undefined' || id === 'null') {
          console.error('❌ ID is invalid!', { 
            id, 
            type: typeof id,
            url: window.location.href,
            pathname: window.location.pathname,
            params: new URLSearchParams(window.location.search).toString()
          });
          console.error('❌ Redirecting to properties page...');
          setProperty(null);
          setLoading(false);
          navigate('/properties');
          return;
        }
        
        const cacheBuster = Date.now();
        // Asegurar que la URL de API siempre tenga /api
        let apiUrl = process.env.REACT_APP_API_URL || 'https://inmobiliariaorsan.com.mx/api';
        if (!apiUrl.endsWith('/api')) {
          apiUrl += '/api';
        }
        const fullUrl = `${apiUrl}/propiedad/${id}?cb=${cacheBuster}`;
        console.log('🌐 Fetching from URL:', fullUrl);
        
        const res = await fetch(fullUrl);
        console.log('Response status:', res.status);
        console.log('Response headers:', Object.fromEntries(res.headers));
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const textResponse = await res.text();
          console.error('❌ Response is not JSON:', textResponse.substring(0, 200) + '...');
          throw new Error('Server returned HTML instead of JSON. Check API configuration.');
        }
        
        const response = await res.json();
        console.log('Property API response:', response);
        
        // Debug específico para propiedad 34
        if (id === '34') {
          console.log('🔍 DEBUG PROPIEDAD 34:');
          console.log('Raw response:', response);
          if (response.data) {
            console.log('📋 Características raw:', response.data.caracteristicas);
            console.log('📸 Fotos raw:', response.data.fotos);
          }
        }
        
        // Handle both data structures for individual property
        let propertyData;
        if (response.success && response.data) {
          // New API structure with wrapper (serverless function)
          propertyData = response.data;
        } else if (response.id || response.id_propiedad) {
          // Direct property object (local server)
          propertyData = response;
        } else {
          console.error('Unexpected property API response structure:', response);
          throw new Error('Invalid property data structure');
        }
        
        // Normalize the property data structure
        const normalizedProperty = {
          id_propiedad: propertyData.id_propiedad || propertyData.id,
          titulo: propertyData.titulo,
          descripcion: propertyData.descripcion,
          precio: propertyData.precio,
          fotos: propertyData.fotos,
          destacado: propertyData.destacado || propertyData.destacada,
          caracteristicas: propertyData.caracteristicas || JSON.stringify({
            modalidad: propertyData.modalidad,
            inmueble: propertyData.inmueble,
            ubicacion: propertyData.ubicacion
          })
        };
        
        console.log('Normalized property data:', normalizedProperty);
        setProperty(normalizedProperty);
      } catch (error) {
        console.error("Error al cargar la propiedad:", error);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (isValidId) {
      fetchProperty();
    } else {
      setLoading(false);
    }
    
    // Fetch all properties for similar properties section
    let propertiesApiUrl = process.env.REACT_APP_API_URL || 'https://inmobiliariaorsan.com.mx/api';
    if (!propertiesApiUrl.endsWith('/api')) {
      propertiesApiUrl += '/api';
    }
    const propertiesUrl = propertiesApiUrl + "/propiedad";
    console.log('🌐 Fetching properties from URL:', propertiesUrl);
    
    fetch(propertiesUrl)
      .then((res) => {
        console.log('Properties response status:', res.status);
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server returned HTML instead of JSON for properties list');
        }
        return res.json();
      })
      .then((response) => {
        console.log('Properties API response:', response);
        
        // Handle both data structures: direct array or wrapped in response object
        let propertiesData;
        if (response.success && response.data) {
          // New API structure with wrapper
          propertiesData = response.data;
        } else if (Array.isArray(response)) {
          // Old API structure - direct array
          propertiesData = response;
        } else {
          console.error('Unexpected API response structure:', response);
          propertiesData = [];
        }
        
        console.log('All properties loaded:', propertiesData.length);
        
        // Normalize the data structure for consistent usage
        const normalizedProperties = propertiesData.map(prop => ({
          id_propiedad: prop.id_propiedad || prop.id,
          titulo: prop.titulo,
          descripcion: prop.descripcion,
          precio: prop.precio,
          fotos: prop.fotos,
          destacado: prop.destacado || prop.destacada,
          caracteristicas: prop.caracteristicas || JSON.stringify({
            modalidad: prop.modalidad,
            inmueble: prop.inmueble,
            ubicacion: prop.ubicacion
          })
        }));
        
        setProperties(normalizedProperties);
      })
      .catch((err) =>
        console.error("Error al cargar propiedades similares:", err)
      );
  }, [id, navigate, isValidId]);

  // Enhanced keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!modalOpen || !property) return;
      
      const fotos = getAllImageUrls(property.fotos);
      
      switch (e.key) {
        case 'ArrowLeft':
        case 'a':
        case 'A':
          e.preventDefault();
          setMainImageIndex(mainImageIndex > 0 ? mainImageIndex - 1 : fotos.length - 1);
          // Add visual feedback
          const leftArrow = document.querySelector('.nav-arrow-left');
          if (leftArrow) {
            leftArrow.style.transform = 'translateY(-50%) scale(1.2)';
            setTimeout(() => {
              leftArrow.style.transform = 'translateY(-50%) scale(1)';
            }, 150);
          }
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          e.preventDefault();
          setMainImageIndex(mainImageIndex < fotos.length - 1 ? mainImageIndex + 1 : 0);
          // Add visual feedback
          const rightArrow = document.querySelector('.nav-arrow-right');
          if (rightArrow) {
            rightArrow.style.transform = 'translateY(-50%) scale(1.2)';
            setTimeout(() => {
              rightArrow.style.transform = 'translateY(-50%) scale(1)';
            }, 150);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setModalOpen(false);
          break;
        case 'Home':
          e.preventDefault();
          setMainImageIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setMainImageIndex(fotos.length - 1);
          break;
        case ' ': // Spacebar
          e.preventDefault();
          setMainImageIndex(mainImageIndex < fotos.length - 1 ? mainImageIndex + 1 : 0);
          break;
        default:
          // Check for number keys (1-9) to jump to specific image
          const num = parseInt(e.key);
          if (num >= 1 && num <= fotos.length) {
            e.preventDefault();
            setMainImageIndex(num - 1);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [modalOpen, mainImageIndex, property]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareMenu && !event.target.closest('.share-section')) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareMenu]);

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div
          style={{
            width: "60px",
            height: "60px",
            border: "6px solid #ccc",
            borderTop: "6px solid #0077cc",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>
          {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
        </style>
      </div>
    );
  
  if (!property) {
    return (
      <Layout style={{ alignItems: "center", fontFamily: "Inclusive Sans" }}>
        <Header active="propiedades" />
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h1 style={{ fontSize: '2rem', color: '#dc2626', marginBottom: '1rem' }}>Propiedad no encontrada</h1>
            <p style={{ fontSize: '1.125rem', color: '#6B7280', marginBottom: '2rem' }}>
              La propiedad que buscas no existe o ha sido eliminada.
            </p>
            <button
              onClick={() => navigate('/properties')}
              style={{
                backgroundColor: '#00921E',
                color: 'white',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#007a1a'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#00921E'}
            >
              Ver todas las propiedades
            </button>
          </div>
        </main>
        <Footer />
      </Layout>
    );
  }

  const fotos = getAllImageUrls(property.fotos);
  const caracteristicas = JSON.parse(property.caracteristicas || "{}");
  const modalidad = caracteristicas?.modalidad?.toUpperCase() || "Modalidad";
  return (
    <Layout style={{ alignItems: "center", fontFamily: "Inclusive Sans" }}>
      <Header active="propiedades" />
      <main style={{flex: 1}}>
      <div className="property-detail-container">
        <div className="property-content">
          {/* Header Section */}
          <div className="property-header">
            <div className="property-title-section">
              <h1 className="property-title">{property.titulo}</h1>
              <div className="property-badges">
                {caracteristicas.inmueble && (
                  <span className="badge badge-type">
                    {caracteristicas.inmueble.toUpperCase()}
                  </span>
                )}
                {caracteristicas.modalidad && (
                  <span className={`badge badge-${modalidad.toLowerCase()}`}>
                    {caracteristicas.modalidad.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            <div className="property-actions">
              <div className="property-price">
                <span className="price-label">Precio</span>
                <span className="price-value">
                  ${parseInt(property.precio).toLocaleString()} MXN
                </span>
              </div>
              <div className="share-section">
                <button 
                  className="share-btn"
                  onClick={() => setShowShareMenu(!showShareMenu)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                  </svg>
                  Compartir
                </button>
                {showShareMenu && (
                  <div className="share-menu">
                    <button onClick={() => shareProperty('whatsapp')}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488"/>
                      </svg>
                      WhatsApp
                    </button>
                    <button onClick={() => shareProperty('facebook')}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </button>
                    <button onClick={() => shareProperty('twitter')}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                      </svg>
                      Twitter
                    </button>
                    <button onClick={() => shareProperty('copy')}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                      </svg>
                      Copiar enlace
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Image Gallery */}
          <div className="image-gallery">
            <div className="main-image-container">
              <img
                src={fotos[mainImageIndex] || ''}
                alt="Imagen principal de la propiedad"
                className="main-image"
                onClick={() => setModalOpen(true)}
                style={{ cursor: 'pointer' }}
              />
              <div className="zoom-indicator">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                  <path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"/>
                </svg>
              </div>
            </div>
            {fotos.length > 1 && (
              <div className="thumbnail-gallery">
                {fotos.map((foto, i) => (
                  <img
                    key={i}
                    src={foto}
                    alt={`Imagen ${i + 1} de la propiedad`}
                    className={`thumbnail ${i === mainImageIndex ? 'active' : ''}`}
                    onClick={() => setMainImageIndex(i)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Contact Button Section */}
          <div className="contact-button-section">
            <div className="contact-button-container">
              <button
                onClick={() =>
                  window.open(
                    `https://api.whatsapp.com/send?phone=522283667786&text=${encodeURIComponent(
                      `¡Hola! Estoy muy interesado en la propiedad "${property.titulo}" con precio de $${parseInt(
                        property.precio
                      ).toLocaleString()} MXN. 

Me gustaría recibir más información y agendar una visita. 

${caracteristicas.ubicacion ? `Ubicación: ${caracteristicas.ubicacion}` : ''}
${caracteristicas.inmueble ? `Tipo: ${caracteristicas.inmueble}` : ''}
${caracteristicas.modalidad ? `Modalidad: ${caracteristicas.modalidad}` : ''}

Puedes ver todos los detalles aquí: ${window.location.href}

¡Espero tu respuesta!`
                    )}`,
                    "_blank"
                  )
                }
                className="contact-main-button"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="contact-icon">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488" fill="currentColor"/>
                </svg>
                <span>¡Contactanos para esta propiedad!</span>
              </button>
            </div>
          </div>

          {/* Property Characteristics Section */}
          <div className="property-characteristics-section">
            <div className="section-header">
              <h2 className="characteristics-main-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                  <path d="M12 2l1.09 3.26L16 6.27l-2.54 2.27L14.18 12 12 10.27 9.82 12l.72-3.46L8 6.27l2.91-1.01L12 2z"/>
                  <circle cx="12" cy="16" r="2"/>
                  <path d="M12 20c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                </svg>
                Características Principales
              </h2>
              <p className="characteristics-subtitle">Descubre todo lo que esta propiedad tiene para ofrecerte</p>
            </div>
            
            {/* Solo mostrar información real de la base de datos */}
            <div className="characteristics-container">
              {/* Ubicación - Solo si existe en la DB */}
              {caracteristicas.ubicacion && (
                <div className="characteristic-item featured">
                  <div className="characteristic-icon location">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Ubicación</span>
                    <span className="characteristic-value">{caracteristicas.ubicacion}</span>
                    <span className="characteristic-description">Ubicación registrada en la base de datos</span>
                  </div>
                </div>
              )}

              {/* Tipo de Inmueble - Solo si existe en la DB */}
              {caracteristicas.inmueble && (
                <div className="characteristic-item">
                  <div className="characteristic-icon property-type">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Tipo de Inmueble</span>
                    <span className="characteristic-value">{caracteristicas.inmueble}</span>
                    <span className="characteristic-description">Tipo de propiedad registrado</span>
                  </div>
                </div>
              )}

              {/* Modalidad - Solo si existe en la DB */}
              {caracteristicas.modalidad && (
                <div className="characteristic-item">
                  <div className="characteristic-icon modality">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Modalidad</span>
                    <span className="characteristic-value">{caracteristicas.modalidad}</span>
                    <span className="characteristic-description">Modalidad registrada en la base de datos</span>
                  </div>
                </div>
              )}

              {/* Baños - Solo si existe en la DB */}
              {caracteristicas.baños && (
                <div className="characteristic-item">
                  <div className="characteristic-icon bathrooms">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 2v2.5c-1.24.56-2 1.78-2 3.22V16c0 2.21 1.79 4 4 4s4-1.79 4-4V7.72c0-1.44-.76-2.66-2-3.22V2H9z"/>
                      <path d="M6 16h12v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-1zm1-7c0-.55.45-1 1-1h8c.55 0 1 .45 1 1v6H7V9z"/>
                      <circle cx="12" cy="6" r="1"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Baños</span>
                    <span className="characteristic-value">{caracteristicas.baños || caracteristicas.banos}</span>
                    <span className="characteristic-description">Número de baños completos</span>
                  </div>
                </div>
              )}

              {/* Cuartos/Recámaras - Solo si existe en la DB */}
              {(caracteristicas.cuartos || caracteristicas.recamaras) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon rooms">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 7H9c-1.1 0-2 .9-2 2v6c0 .55.45 1 1 1h1v3h2v-3h6v3h2v-3h1c.55 0 1-.45 1-1V9c0-1.1-.9-2-2-2zm-8 2h6v4H11V9z"/>
                      <circle cx="6" cy="6" r="2"/>
                      <path d="M7 10v2H5v-2c0-.55.45-1 1-1s1 .45 1 1z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Habitaciones</span>
                    <span className="characteristic-value">{caracteristicas.cuartos || caracteristicas.recamaras}</span>
                    <span className="characteristic-description">Número de habitaciones</span>
                  </div>
                </div>
              )}

              {/* Cochera */}
              {(caracteristicas.cochera === true || caracteristicas.cochera === "true" || caracteristicas.cochera === 1) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon garage">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5 11l1.5-5h11L19 11H5z"/>
                      <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99z"/>
                      <circle cx="6.5" cy="15.5" r="1.5"/>
                      <circle cx="17.5" cy="15.5" r="1.5"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Cochera</span>
                    <span className="characteristic-value">Disponible</span>
                    <span className="characteristic-description">Estacionamiento incluido</span>
                  </div>
                </div>
              )}

              {/* Cocina */}
              {(caracteristicas.cocina === true || caracteristicas.cocina === "true" || caracteristicas.cocina === 1) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon kitchen">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 2.01L6 2c-1.1 0-2 .89-2 2v16c0 1.11.9 2 2 2h12c1.1 0 2-.89 2-2V4c0-1.11-.9-1.99-2-1.99zM18 20H6v-9.02h12V20zm0-11H6V4h12v5z"/>
                      <circle cx="8" cy="7" r="1"/>
                      <circle cx="12" cy="7" r="1"/>
                      <circle cx="16" cy="7" r="1"/>
                      <rect x="7" y="13" width="10" height="2" rx="1"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Cocina</span>
                    <span className="characteristic-value">Equipada</span>
                    <span className="characteristic-description">Cocina incluida</span>
                  </div>
                </div>
              )}

              {/* Jardín */}
              {(caracteristicas.jardin === true || caracteristicas.jardin === "true" || caracteristicas.jardin === 1) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon garden">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9z"/>
                      <path d="M5.6 10.25c0-1.38 1.12-2.5 2.5-2.5.53 0 1.01.16 1.42.44l-.02.19c0 1.38-1.12 2.5-2.5 2.5-.53 0-1.01-.16-1.4-.44v-.19z"/>
                      <path d="M7.5 12c0-1.38 1.12-2.5 2.5-2.5.53 0 1.01.16 1.42.44 0 1.38-1.12 2.5-2.5 2.5-.53 0-1.01-.16-1.42-.44z"/>
                      <path d="M9.5 14c0-1.38 1.12-2.5 2.5-2.5.53 0 1.01.16 1.42.44 0 1.38-1.12 2.5-2.5 2.5-.53 0-1.01-.16-1.42-.44z"/>
                      <path d="M12 2l1.09 3.26L16 4.27l-2.54 2.27L14.18 10 12 8.27 9.82 10l.72-3.46L8 4.27l2.91-.01L12 2z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Jardín</span>
                    <span className="characteristic-value">Disponible</span>
                    <span className="characteristic-description">Área verde incluida</span>
                  </div>
                </div>
              )}

              {/* Amueblado */}
              {(caracteristicas.amueblado === true || caracteristicas.amueblado === "true" || caracteristicas.amueblado === 1) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon furnished">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H9v-7H2v9h7v-2h2v2h11V7z"/>
                      <rect x="4" y="15" width="16" height="2" rx="1"/>
                      <rect x="6" y="17" width="2" height="4" rx="1"/>
                      <rect x="16" y="17" width="2" height="4" rx="1"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Amueblado</span>
                    <span className="characteristic-value">Sí</span>
                    <span className="characteristic-description">Incluye mobiliario</span>
                  </div>
                </div>
              )}

              {/* Piscina */}
              {(caracteristicas.piscina === true || caracteristicas.piscina === "true" || caracteristicas.piscina === 1) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon pool">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2 17h20v2H2zm1.15-4.05L4 11l.85 1.95L6 11l1.15 1.95L8 11l.85 1.95L10 11l1.15 1.95L12 11l.85 1.95L14 11l1.15 1.95L16 11l.85 1.95L18 11l.85 1.95L20 11v2l-.85 1.95L18 13l-1.15 1.95L16 13l-1.15 1.95L14 13l-1.15 1.95L12 13l-1.15 1.95L10 13l-1.15 1.95L8 13l-1.15 1.95L6 13l-1.15 1.95L4 13l-.85 1.95L2 13v-2l.85-1.95L4 11z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Piscina</span>
                    <span className="characteristic-value">Disponible</span>
                    <span className="characteristic-description">Área de alberca</span>
                  </div>
                </div>
              )}

              {/* Terraza */}
              {(caracteristicas.terraza === true || caracteristicas.terraza === "true" || caracteristicas.terraza === 1) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon terrace">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 8V6l-3-3H6L3 6v2l1 1v9h16V9l1-1zM7.5 7.5h9v1h-9v-1zM5 17V9h14v8H5z"/>
                      <rect x="7" y="11" width="10" height="1"/>
                      <rect x="8" y="13" width="8" height="1"/>
                      <path d="M9 15h6c.55 0 1 .45 1 1v1H8v-1c0-.55.45-1 1-1z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Terraza</span>
                    <span className="characteristic-value">Disponible</span>
                    <span className="characteristic-description">Área exterior techada</span>
                  </div>
                </div>
              )}

              {/* Cocina Integrada */}
              {(caracteristicas.cocina_integrada === true || caracteristicas.cocina_integrada === "true" || caracteristicas.cocina_integrada === 1 || caracteristicas["cocina integrada"] === true || caracteristicas["cocina integrada"] === "true" || caracteristicas["cocina integrada"] === 1) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon integrated-kitchen">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 2.01L6 2c-1.1 0-2 .89-2 2v16c0 1.11.9 2 2 2h12c1.1 0 2-.89 2-2V4c0-1.11-.9-1.99-2-1.99zM18 20H6v-9.02h12V20zm0-11H6V4h12v5zm-5.5 2.5h3V15h-3zm-6 0h3V15h-3z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Cocina Integrada</span>
                    <span className="characteristic-value">Disponible</span>
                    <span className="characteristic-description">Cocina moderna integrada</span>
                  </div>
                </div>
              )}

              {/* Garage/Cochera (alternativa para garage) */}
              {(caracteristicas.garage === true || caracteristicas.garage === "true" || caracteristicas.garage === 1) && !(caracteristicas.cochera === true || caracteristicas.cochera === "true" || caracteristicas.cochera === 1) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon garage">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 15v4H5v-4h14m1-2H4c-.55 0-1 .45-1 1v6h18v-6c0-.55-.45-1-1-1zM7 18.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5.5.22.5.5-.22.5-.5.5zm10 0c-.28 0-.5-.22-.5-.5s.22-.5.5-.5.5.22.5.5-.22.5-.5.5z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Garage</span>
                    <span className="characteristic-value">Disponible</span>
                    <span className="characteristic-description">Estacionamiento techado</span>
                  </div>
                </div>
              )}

              {/* Servicios */}
              {(caracteristicas.servicios === true || caracteristicas.servicios === "true" || caracteristicas.servicios === 1) && (
                <div className="characteristic-item">
                  <div className="characteristic-icon services">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14.12 4L12 2L9.88 4H8.12L6.12 2L4 4l2 2.12V8.12l-2 2L6.12 12l2-2h1.76L12 12l2.12-2h1.76l2 2L20 10.12l-2-2V6.12l2-2L18.12 2 16.12 4h-2z"/>
                      <circle cx="9" cy="9" r="2"/>
                      <circle cx="15" cy="15" r="2"/>
                      <path d="M12 16c-2.21 0-4 1.79-4 4h8c0-2.21-1.79-4-4-4z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Servicios</span>
                    <span className="characteristic-value">Completos</span>
                    <span className="characteristic-description">Agua, luz, gas incluidos</span>
                  </div>
                </div>
              )}

              {/* Tamaño */}
              {caracteristicas.tamaño && (
                <div className="characteristic-item">
                  <div className="characteristic-icon size">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 17h2v2H3v-2zm0-4h2v2H3v-2zm2-2H3v-2h2v2zM7 19h2v-2H7v2zm0-4h2v-2H7v2zm0-4h2V9H7v2zm4 8h2v-2h-2v2zm0-4h2v-2h-2v2zm0-4h2V9h-2v2zm8 8h2v-2h-2v2zm0-4h2v-2h-2v2zm0-4h2V9h-2v2z"/>
                      <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 14H5V7h14v10z"/>
                    </svg>
                  </div>
                  <div className="characteristic-content">
                    <span className="characteristic-label">Tamaño</span>
                    <span className="characteristic-value">{caracteristicas.tamaño}m²</span>
                    <span className="characteristic-description">Metros cuadrados</span>
                  </div>
                </div>
              )}

              {/* Características adicionales dinámicas */}
              {Object.entries(caracteristicas).map(([key, value]) => {
                // Skip características ya mostradas
                const skipKeys = ['ubicacion', 'inmueble', 'modalidad', 'cuartos', 'recamaras', 'baños', 'banos', 'cochera', 'cocina', 'jardin', 'amueblado', 'servicios', 'tamaño', 'piscina', 'terraza', 'cocina_integrada', 'cocina integrada', 'garage'];
                if (skipKeys.includes(key.toLowerCase())) return null;
                
                // Solo mostrar si es true, "true", 1 o tiene un valor válido
                if ((value === true || value === "true" || value === 1 || (typeof value === 'string' && value.trim() !== '' && value !== 'false')) && value !== false && value !== 0) {
                  return (
                    <div key={key} className="characteristic-item">
                      <div className="characteristic-icon additional">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l1.09 3.26L16 6.27l-2.54 2.27L14.18 12 12 10.27 9.82 12l.72-3.46L8 6.27l2.91-1.01L12 2z"/>
                          <circle cx="12" cy="16" r="2"/>
                          <path d="M12 20c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                        </svg>
                      </div>
                      <div className="characteristic-content">
                        <span className="characteristic-label">{key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}</span>
                        <span className="characteristic-value">
                          {(value === true || value === "true" || value === 1) ? 'Disponible' : value}
                        </span>
                        <span className="characteristic-description">Característica adicional</span>
                      </div>
                    </div>
                  );
                }
                return null;
              }).filter(Boolean)}
            </div>
          </div>

          {/* Property Description */}
          <div className="property-description-section">
            <div className="description-header">
              <h2 className="section-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                </svg>
                Descripción de la Propiedad
              </h2>
              <p className="section-subtitle">Conoce todos los detalles que hacen especial esta propiedad</p>
            </div>
            
            <div className="description-content">
              <div className="description-card">
                <div className="description-text">
                  <div className="description-pre-container">
                    <pre className="description-pre">{property.descripcion}</pre>
                  </div>
                </div>


              </div>

              <div className="contact-section">
                <div className="contact-card">
                  <div className="contact-header">
                    <h3 className="contact-title">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                        <path d="M7.5 4v15l4.5-2.25L16.5 19V4H7.5zM6 2h12a1 1 0 011 1v18l-6-3-6 3V3a1 1 0 011-1z"/>
                      </svg>
                      ¿Te interesa esta propiedad?
                    </h3>
                    <p className="contact-subtitle">Contacta con nosotros para más información o para agendar una visita</p>
                  </div>
                  
                  <div className="contact-actions">
                    <button
                      onClick={() =>
                        window.open(
                          `https://api.whatsapp.com/send?phone=522283667786&text=${encodeURIComponent(
                            `🏡 ¡Hola! Estoy muy interesado en la propiedad "${property.titulo}" con precio de $${parseInt(
                              property.precio
                            ).toLocaleString()} MXN. 

Me gustaría recibir más información y agendar una visita. 

${caracteristicas.ubicacion ? `📍 Ubicación: ${caracteristicas.ubicacion}` : ''}
${caracteristicas.inmueble ? `🏠 Tipo: ${caracteristicas.inmueble}` : ''}
${caracteristicas.modalidad ? `💼 Modalidad: ${caracteristicas.modalidad}` : ''}

Puedes ver todos los detalles aquí: ${window.location.href}

¡Espero tu respuesta! 😊`
                          )}`,
                          "_blank"
                        )
                      }
                      className="whatsapp-contact-button primary"
                    >
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="whatsapp-icon">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488" fill="currentColor"/>
                      </svg>
                      <span>Contactar por WhatsApp</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        const phoneNumber = "522283667786";
                        window.open(`tel:${phoneNumber}`, "_self");
                      }}
                      className="phone-contact-button secondary"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                      </svg>
                      <span>Llamar Ahora</span>
                    </button>
                  </div>
                  
                  <div className="agent-info">
                    <div className="agent-avatar">🏢</div>
                    <div className="agent-details">
                      <span className="agent-name">Inmobiliaria ORSAN</span>
                      <span className="agent-role">Expertos en bienes raíces</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Similar Properties Section */}
      <section className="similar-properties-section">
        <div className="similar-properties-container">
          <div className="similar-properties-header">
            <h2 className="similar-properties-title">🏘️ Propiedades Relacionadas por Ubicación</h2>
            <p className="similar-properties-subtitle">
              {caracteristicas.ubicacion 
                ? (() => {
                    const locationParts = caracteristicas.ubicacion.split(',');
                    const city = locationParts.length > 1 ? locationParts[1]?.trim() : locationParts[0]?.trim();
                    return `Propiedades relacionadas con ${city} - incluyendo opciones destacadas y cercanas`;
                  })()
                : 'Descubre propiedades similares y relacionadas en nuestra selección'
              }
            </p>
          </div>
          
          <div className="similar-properties-content">
            {(() => {
              // Lógica mejorada para encontrar propiedades relacionadas
              const getRelatedProperties = () => {
                const currentLocation = caracteristicas.ubicacion?.split(',');
                const currentCity = currentLocation?.length > 1 
                  ? currentLocation[1]?.trim()?.toLowerCase() 
                  : currentLocation?.[0]?.trim()?.toLowerCase();
                
                let relatedProperties = [];
                
                // 1. Primero: Propiedades destacadas en la misma ciudad
                const featuredInCity = properties.filter(p => {
                  if (p.id_propiedad === parseInt(id)) return false;
                  const pCaracteristicas = JSON.parse(p.caracteristicas || '{}');
                  const propertyCity = pCaracteristicas.ubicacion?.toLowerCase() || '';
                  return p.destacado && (!currentCity || propertyCity.includes(currentCity));
                });
                
                relatedProperties = [...featuredInCity];
                
                // 2. Si no hay suficientes destacadas, agregar no destacadas de la misma ciudad
                if (relatedProperties.length < 4) {
                  const nonFeaturedInCity = properties.filter(p => {
                    if (p.id_propiedad === parseInt(id)) return false;
                    if (relatedProperties.some(rp => rp.id_propiedad === p.id_propiedad)) return false;
                    const pCaracteristicas = JSON.parse(p.caracteristicas || '{}');
                    const propertyCity = pCaracteristicas.ubicacion?.toLowerCase() || '';
                    return !p.destacado && (!currentCity || propertyCity.includes(currentCity));
                  });
                  
                  relatedProperties = [...relatedProperties, ...nonFeaturedInCity];
                }
                
                // 3. Si aún no hay suficientes, buscar en ubicaciones relacionadas (Xalapa)
                if (relatedProperties.length < 4 && currentCity) {
                  const relatedLocations = ['xalapa', 'coatepec', 'banderilla', 'veracruz', 'puebla'];
                  const currentLocationIndex = relatedLocations.findIndex(loc => 
                    currentCity.includes(loc) || loc.includes(currentCity)
                  );
                  
                  if (currentLocationIndex !== -1) {
                    const nearbyLocations = properties.filter(p => {
                      if (p.id_propiedad === parseInt(id)) return false;
                      if (relatedProperties.some(rp => rp.id_propiedad === p.id_propiedad)) return false;
                      const pCaracteristicas = JSON.parse(p.caracteristicas || '{}');
                      const propertyCity = pCaracteristicas.ubicacion?.toLowerCase() || '';
                      return relatedLocations.some(loc => propertyCity.includes(loc));
                    });
                    
                    relatedProperties = [...relatedProperties, ...nearbyLocations];
                  }
                }
                
                // 4. Si aún no hay suficientes, mostrar cualquier propiedad disponible
                if (relatedProperties.length < 4) {
                  const anyAvailable = properties.filter(p => {
                    if (p.id_propiedad === parseInt(id)) return false;
                    return !relatedProperties.some(rp => rp.id_propiedad === p.id_propiedad);
                  });
                  
                  relatedProperties = [...relatedProperties, ...anyAvailable];
                }
                
                return relatedProperties.slice(0, 4);
              };
              
              const relatedProperties = getRelatedProperties();
              
              return relatedProperties.length > 0 ? (
                <div className="similar-properties-scroll">
                  {relatedProperties.map((p, i) => (
                    <div
                      key={i}
                      className="similar-property-card enhanced"
                      onClick={() => {
                        if (!p.id_propiedad) {
                          console.error('❌ Similar property has no valid ID:', p);
                          return;
                        }
                        window.location.href = `/propiedad/${p.id_propiedad}`;
                        window.scrollTo(0, 0);
                      }}
                    >
                      <div className="similar-property-image">
                        <img 
                          src={getFirstImageUrl(p.fotos)}
                          alt={p.titulo}
                          loading="lazy"
                        />
                        <div className="property-overlay">
                          <div className="property-badge">
                            {JSON.parse(p.caracteristicas || '{}').modalidad?.toUpperCase() || 'VENTA'}
                          </div>
                          {p.destacado && (
                            <div className="featured-badge">⭐ Destacada</div>
                          )}
                          <button className="quick-view-btn">
                            👁️ Ver Detalles
                          </button>
                        </div>
                      </div>
                      
                      <div className="similar-property-content">
                        <h3 className="similar-property-title">{p.titulo}</h3>
                        <div className="similar-property-location">
                          📍 {JSON.parse(p.caracteristicas || '{}').ubicacion || 'Ubicación disponible'}
                        </div>
                        
                        <div className="location-relation">
                          {(() => {
                            const currentLocation = caracteristicas.ubicacion?.split(',');
                            const currentCity = currentLocation?.length > 1 
                              ? currentLocation[1]?.trim()?.toLowerCase() 
                              : currentLocation?.[0]?.trim()?.toLowerCase();
                            
                            const pCaracteristicas = JSON.parse(p.caracteristicas || '{}');
                            const propertyCity = pCaracteristicas.ubicacion?.toLowerCase() || '';
                            
                            if (currentCity && propertyCity.includes(currentCity)) {
                              return <span className="relation-same-city">🏙️ Misma ciudad</span>;
                            } else if (currentCity && (propertyCity.includes('xalapa') || propertyCity.includes('coatepec') || propertyCity.includes('banderilla'))) {
                              return <span className="relation-nearby">📍 Cerca de tu ubicación</span>;
                            } else {
                              return <span className="relation-other">🌍 Otra ubicación</span>;
                            }
                          })()}
                        </div>
                        
                        <div className="similar-property-features">
                          {JSON.parse(p.caracteristicas || '{}').inmueble && (
                            <div className="feature-item">
                              <span className="feature-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                                </svg>
                              </span>
                              <span>{JSON.parse(p.caracteristicas || '{}').inmueble}</span>
                            </div>
                          )}
                          {JSON.parse(p.caracteristicas || '{}').modalidad && (
                            <div className="feature-item">
                              <span className="feature-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  {JSON.parse(p.caracteristicas || '{}').modalidad === 'venta' ? (
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                  ) : (
                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
                                  )}
                                </svg>
                              </span>
                              <span>{JSON.parse(p.caracteristicas || '{}').modalidad}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="similar-property-price">
                          <span className="price-amount">${parseInt(p.precio).toLocaleString()}</span>
                          <span className="price-currency">MXN</span>
                        </div>
                        
                        <div className="similar-property-actions">
                          <button 
                            className="action-btn primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(
                                `https://api.whatsapp.com/send?phone=522283667786&text=${encodeURIComponent(
                                  `¡Hola! Me interesa la propiedad "${p.titulo}" por $${parseInt(p.precio).toLocaleString()} MXN. ¿Podrías darme más información?`
                                )}`,
                                "_blank"
                              );
                            }}
                          >
                            WhatsApp
                          </button>
                          <button 
                            className="action-btn secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!p.id_propiedad) {
                                console.error('❌ Property has no valid ID for navigation:', p);
                                return;
                              }
                              window.location.href = `/propiedad/${p.id_propiedad}`;
                            }}
                          >
                            👀 Ver Más
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-properties-message">
                  <div className="no-properties-icon">🏠</div>
                  <h3>No hay más propiedades disponibles</h3>
                  <p>Te invitamos a explorar nuestro catálogo completo de propiedades en Xalapa y la región.</p>
                  <button 
                    className="browse-all-btn"
                    onClick={() => navigate('/properties')}
                  >
                    🔍 Ver Todas las Propiedades
                  </button>
                </div>
              );
            })()}
          </div>
          
          <div className="view-all-properties">
            <button 
              className="view-all-btn"
              onClick={() => navigate('/properties')}
            >
              🏘️ Ver Todas Nuestras Propiedades
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Image Zoom Modal */}
      {modalOpen && (
        <div className="image-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close-btn"
              onClick={() => setModalOpen(false)}
              aria-label="Cerrar modal"
            >
              ✕
            </button>
            
            <div className="modal-image-container">
              <img
                src={fotos[mainImageIndex] || ''}
                alt={`Imagen ${mainImageIndex + 1} de ${fotos.length}`}
                className="modal-main-image"
              />
              
              {fotos.length > 1 && (
                <>
                  <button 
                    className="nav-arrow nav-arrow-left"
                    onClick={() => setMainImageIndex(mainImageIndex > 0 ? mainImageIndex - 1 : fotos.length - 1)}
                    aria-label="Imagen anterior"
                  >
                    ‹
                  </button>
                  <button 
                    className="nav-arrow nav-arrow-right"
                    onClick={() => setMainImageIndex(mainImageIndex < fotos.length - 1 ? mainImageIndex + 1 : 0)}
                    aria-label="Siguiente imagen"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            
            <div className="modal-image-info">
              <span className="image-counter">
                {mainImageIndex + 1} / {fotos.length}
              </span>
              <div className="modal-thumbnails">
                {fotos.map((foto, i) => (
                  <img
                    key={i}
                    src={foto}
                    alt={`Miniatura ${i + 1}`}
                    className={`modal-thumbnail ${i === mainImageIndex ? 'active' : ''}`}
                    onClick={() => setMainImageIndex(i)}
                  />
                ))}
              </div>
            </div>
            
            
          </div>
        </div>
      )}

      </main>

      <div className="footer-wrapper">
        <Footer />
      </div>
    </Layout>
  );
}
